import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, User, Save, X, AlertCircle, CheckCircle, MailCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { userService } from '../services/userService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import permissionService from '../services/permissionService';
import invitationService from '../services/invitationService';
import './UserSettings.css';

const UserSettings = () => {
  const { user } = useAuth();
  const { selectedAccount } = useAccount();
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState(null);
  const [accountName, setAccountName] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    assignedHotels: [],
  });

  useEffect(() => {
    if (selectedAccount) {
      loadUsers();
      loadHotels();
      loadAccountName();
    }
  }, [selectedAccount]);

  const loadUsers = async () => {
    if (!selectedAccount) {
      setMessage({ type: 'error', text: 'Please select a hotel chain from the dropdown above' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const response = await userService.getAllUsers({ accountId: selectedAccount });
      setUsers(response.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to load users' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadHotels = async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await hotelService.getAllHotels({ accountid: selectedAccount });
      setHotels(response.hotels || []);
    } catch (error) {
      console.error('Error loading hotels:', error);
    }
  };

  const loadAccountName = async () => {
    if (!selectedAccount) return;
    
    try {
      const response = await accountService.getAccountById(selectedAccount);
      const account = response.account || response;
      setAccountName(account.name || '');
    } catch (error) {
      console.error('Error loading account name:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAccount) {
      setMessage({ type: 'error', text: 'Please select a hotel chain first' });
      return;
    }

    // Validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setMessage({ type: 'error', text: 'Email, First Name, and Last Name are required' });
      return;
    }

    if (!formData.assignedHotels || (Array.isArray(formData.assignedHotels) && formData.assignedHotels.length === 0)) {
      setMessage({ type: 'error', text: 'Please assign at least one hotel or select "All Hotels"' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      if (editingUser) {
        // Update existing user
        const userData = {
          firstname: formData.firstName,
          lastname: formData.lastName,
        };
        await userService.updateUser(editingUser.ID, userData);

        // Update permissions: diff old vs new assignments
        // Existing permissions remain; new assignments become invitations; removed assignments are deleted
        const existingPermissionsResponse = await permissionService.getPermissionsByUserId(editingUser.ID);
        const existingPermissions = existingPermissionsResponse.permissions || [];

        if (formData.assignedHotels === 'All') {
          const hasChainPermission = existingPermissions.some(p => p.hotel_chain_id);
          // Cancel any pending account invitations for this chain (legacy cleanup)
          const invResponse = await invitationService.getInvitationsByUserId(editingUser.ID);
          const pendingAccountInvites = (invResponse.invitations || []).filter(
            i => i.type === 'account' && Number(i.resource_id) === Number(selectedAccount) && i.status === 'pending'
          );
          await Promise.all(pendingAccountInvites.map(i => invitationService.cancelInvitation(i.id)));

          if (!hasChainPermission) {
            // Remove individual hotel permissions and create chain-level permission
            const hotelPermissions = existingPermissions.filter(p => p.hotelid);
            await Promise.all(hotelPermissions.map(p => permissionService.deletePermission(p.id)));
            await permissionService.createPermission({
              userid: editingUser.ID,
              permission_type: 'A',
              hotel_chain_id: selectedAccount,
              hotelid: null
            });
          } else {
            // Remove any individual hotel permissions no longer needed
            const hotelPermissions = existingPermissions.filter(p => p.hotelid);
            await Promise.all(hotelPermissions.map(p => permissionService.deletePermission(p.id)));
          }
        } else if (Array.isArray(formData.assignedHotels)) {
          const newHotelIds = formData.assignedHotels.map(h => typeof h === 'object' ? h.hotelid : h);
          const existingHotelIds = existingPermissions.filter(p => p.hotelid).map(p => p.hotelid);
          const existingChainPermissions = existingPermissions.filter(p => p.hotel_chain_id);

          // Remove chain-level permissions if switching to individual hotels
          await Promise.all(existingChainPermissions.map(p => permissionService.deletePermission(p.id)));

          // Remove permissions for hotels no longer assigned
          const toRemove = existingPermissions.filter(p => p.hotelid && !newHotelIds.includes(p.hotelid));
          await Promise.all(toRemove.map(p => permissionService.deletePermission(p.id)));

          // Create permissions for newly added hotels
          const toAdd = formData.assignedHotels.filter(h => {
            const hotelId = typeof h === 'object' ? h.hotelid : h;
            return !existingHotelIds.includes(hotelId);
          });
          await Promise.all(toAdd.map(hotelAssignment => {
            const hotelId = typeof hotelAssignment === 'object' ? hotelAssignment.hotelid : hotelAssignment;
            const permission = typeof hotelAssignment === 'object' ? hotelAssignment.permission : 'User';
            return permissionService.createPermission({
              userid: editingUser.ID,
              permission_type: permission === 'Admin' ? 'A' : 'U',
              hotel_chain_id: null,
              hotelid: hotelId
            });
          }));
        }

        // Reload users list to ensure consistent field names
        await loadUsers();
        setMessage({ type: 'success', text: 'User updated successfully' });
      } else {
        // Create new user with Cognito, database record, and permissions
        // Cognito will generate and email a temporary password to the user

        // Step 1: Create user in Cognito and database
        const userData = {
          email: formData.email,
          firstname: formData.firstName,
          lastname: formData.lastName,
          ACCOUNTID: selectedAccount
        };

        const response = await userService.createUser(userData);
        console.log('User creation response:', response);

        const userId = response.insertId;
        if (!userId) {
          throw new Error('Failed to get user ID from response');
        }

        // Step 2: Create permissions for assigned hotels/chain
        if (formData.assignedHotels === 'All') {
          await permissionService.createPermission({
            userid: userId,
            permission_type: 'A',
            hotel_chain_id: selectedAccount,
            hotelid: null
          });
        } else if (Array.isArray(formData.assignedHotels)) {
          await Promise.all(formData.assignedHotels.map(hotelAssignment => {
            const hotelId = typeof hotelAssignment === 'object' ? hotelAssignment.hotelid : hotelAssignment;
            const permission = typeof hotelAssignment === 'object' ? hotelAssignment.permission : 'User';
            return permissionService.createPermission({
              userid: userId,
              permission_type: permission === 'Admin' ? 'A' : 'U',
              hotel_chain_id: null,
              hotelid: hotelId
            });
          }));
        }

        // Reload users list
        await loadUsers();
        const successText = response.alreadyExisted
          ? 'User already exists. Permissions have been updated.'
          : 'User created successfully. A temporary password has been sent to their email.';
        setMessage({ type: 'success', text: successText });
      }

      setShowCreateForm(false);
      setEditingUser(null);
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      let errorMessage = 'Failed to save user';

      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please sign in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Admin access required.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (userData) => {
    setEditingUser(userData);

    let assignedHotels = [];
    try {
      const [permResponse, invResponse] = await Promise.all([
        permissionService.getPermissionsByUserId(userData.ID),
        invitationService.getInvitationsByUserId(userData.ID)
      ]);
      const permissions = permResponse.permissions || [];
      const pendingInvites = (invResponse.invitations || []).filter(i => i.status === 'pending');

      const hasChainPermission = permissions.some(p => p.hotel_chain_id);
      const hasPendingAccountInvite = pendingInvites.some(
        i => i.type === 'account' && Number(i.resource_id) === Number(selectedAccount)
      );

      if (hasChainPermission || hasPendingAccountInvite) {
        assignedHotels = 'All';
      } else {
        const permHotelIds = new Set(permissions.filter(p => p.hotelid).map(p => p.hotelid));
        assignedHotels = [
          ...permissions
            .filter(p => p.hotelid)
            .map(p => ({ hotelid: p.hotelid, permission: p.permission_type === 'A' ? 'Admin' : 'User' })),
          ...pendingInvites
            .filter(i => i.type === 'hotel' && !permHotelIds.has(i.resource_id))
            .map(i => ({ hotelid: i.resource_id, permission: i.permission_type === 'A' ? 'Admin' : 'User' }))
        ];
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }

    setFormData({
      email: userData.loginname || '',
      firstName: userData.firstname || '',
      lastName: userData.lastname || '',
      assignedHotels: assignedHotels,
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      await userService.deleteUser(userId, selectedAccount);
      setUsers(prev => prev.filter(u => u.ID !== userId));
      setMessage({ type: 'success', text: 'User removed successfully' });
    } catch (error) {
      console.error('Error deleting user:', error);
      let errorMessage = 'Failed to delete user';
      
      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Admin access required.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      assignedHotels: [],
    });
  };

  const handleHotelAssignmentChange = (hotelId, checked) => {
    if (formData.assignedHotels === 'All') {
      setFormData(prev => ({
        ...prev,
        assignedHotels: checked ? [{ hotelid: hotelId, permission: 'User' }] : []
      }));
    } else {
      const currentHotels = Array.isArray(formData.assignedHotels) ? formData.assignedHotels : [];
      if (checked) {
        setFormData(prev => ({
          ...prev,
          assignedHotels: [...currentHotels, { hotelid: hotelId, permission: 'User' }]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          assignedHotels: currentHotels.filter(h =>
            typeof h === 'object' ? h.hotelid !== hotelId : h !== hotelId
          )
        }));
      }
    }
  };

  const handlePermissionChange = (hotelId, permission) => {
    const currentHotels = Array.isArray(formData.assignedHotels) ? formData.assignedHotels : [];
    setFormData(prev => ({
      ...prev,
      assignedHotels: currentHotels.map(h => {
        const id = typeof h === 'object' ? h.hotelid : h;
        if (id === hotelId) {
          return { hotelid: hotelId, permission };
        }
        return typeof h === 'object' ? h : { hotelid: h, permission: 'User' };
      })
    }));
  };

  const handleAllHotelsChange = (checked) => {
    if (checked) {
      setFormData(prev => ({ 
        ...prev, 
        assignedHotels: 'All'
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        assignedHotels: []
      }));
    }
  };

  const handleReinvite = async (e, userId) => {
    e.stopPropagation();
    setSaving(true);
    setMessage(null);
    try {
      await userService.reinviteUser(userId);
      setMessage({ type: 'success', text: 'Invitation resent successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to resend invitation' });
    } finally {
      setSaving(false);
    }
  };

  const getUserStatusLabel = (cognitoStatus, hasPendingInvitation) => {
    if (cognitoStatus === 'FORCE_CHANGE_PASSWORD') return { label: 'Invited', className: 'user-status-invited' };
    if (hasPendingInvitation) return { label: 'Invitation Pending', className: 'user-status-pending' };
    return null;
  };

  const filteredUsers = users.filter(userData =>
    (userData.loginname?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (userData.firstname?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (userData.lastname?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const getAssignedHotelsDisplay = (assignedHotels) => {
    if (assignedHotels === 'All') return 'All Hotels (Admin)';
    if (Array.isArray(assignedHotels) && assignedHotels.length === 0) return 'None';
    if (Array.isArray(assignedHotels)) {
      const hotelDetails = assignedHotels.map(item => {
        const hotelId = typeof item === 'object' ? item.hotelid : item;
        const permission = typeof item === 'object' ? item.permission : 'User';
        const hotel = hotels.find(h => h.hotelid === hotelId);
        const hotelName = hotel ? hotel.hotelname : hotelId;
        return `${hotelName} (${permission})`;
      });
      return hotelDetails.join(', ');
    }
    return 'Unknown';
  };

  if (!selectedAccount) {
    return (
      <div className="user-settings-container">
        <div className="no-account-selected">
          <User className="no-account-icon" />
          <h3>No Hotel Chain Selected</h3>
          <p>Please select a hotel chain from the dropdown in the navigation bar to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-settings-container">
      {message && (
        <div className={`user-message ${message.type === 'success' ? 'success' : 'error'}`}>
          <div className="user-message-content">
            <div className="user-message-icon">
              {message.type === 'success' ? (
                <CheckCircle className="icon-success" />
              ) : (
                <AlertCircle className="icon-error" />
              )}
            </div>
            <div className="user-message-text">
              <p>{message.text}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      {!showCreateForm && (
        <div className="user-search-box">
          <div className="user-search-input-wrapper">
            <Search className="user-search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="user-search-input"
            />
          </div>
          <button
            onClick={() => {
              setShowCreateForm(true);
              setEditingUser(null);
              resetForm();
            }}
            className="btn-add-user"
          >
            <Plus className="btn-icon" />
            Add User
          </button>
        </div>
      )}

      {/* Users List */}
      {!showCreateForm && (
        <div className="user-list-container">
          {loading ? (
            <div className="user-loading">
              <div className="user-spinner"></div>
            </div>
          ) : users.length > 0 ? (
            <div className="user-table-wrapper">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th className="user-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((userData) => (
                    <tr
                      key={userData.ID}
                      onClick={() => handleEdit(userData)}
                      className="user-table-row"
                    >
                      <td>
                        <div className="user-email-cell">
                          {userData.loginname || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div className="user-name-cell">
                          {userData.firstname || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div className="user-name-cell">
                          {userData.lastname || 'N/A'}
                        </div>
                      </td>
                      <td>
                        <div className="user-date">
                          {userData.insertdate ? new Date(userData.insertdate).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const status = getUserStatusLabel(userData.cognito_status, userData.has_pending_invitation);
                          return status ? (
                            <span className={`user-status-badge ${status.className}`}>{status.label}</span>
                          ) : null;
                        })()}
                      </td>
                      <td className="user-actions-col">
                        <div className="user-action-buttons">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(userData);
                            }}
                            className="btn-edit-user"
                            title="Edit"
                          >
                            <Edit className="action-icon" />
                          </button>
                          {userData.cognito_status === 'FORCE_CHANGE_PASSWORD' && (
                            <button
                              onClick={(e) => handleReinvite(e, userData.ID)}
                              disabled={saving}
                              className="btn-reinvite-user"
                              title="Re-invite"
                            >
                              <MailCheck className="action-icon" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(userData.ID);
                            }}
                            disabled={saving}
                            className="btn-delete-user"
                            title="Delete"
                          >
                            <Trash2 className="action-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="user-empty-state">
              <User className="empty-state-icon" />
              <h3>No users found</h3>
              <p>
                {searchTerm ? 'Try adjusting your search.' : 'Get started by adding your first user.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit User Form */}
      {showCreateForm && (
        <div className="user-form-container">
          <div className="user-form-header">
            <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            <div className="user-form-header-actions">
              {editingUser?.cognito_status === 'FORCE_CHANGE_PASSWORD' && (
                <button
                  type="button"
                  onClick={(e) => handleReinvite(e, editingUser.ID)}
                  disabled={saving}
                  className="btn-reinvite-form"
                  title="Re-send invitation email"
                >
                  <MailCheck className="btn-icon" />
                  Re-invite
                </button>
              )}
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingUser(null);
                  resetForm();
                }}
                className="btn-close-form"
              >
                <X className="close-icon" />
              </button>
            </div>
          </div>
          <div className="user-form-content">
            <form onSubmit={handleSubmit} className="user-form">
              <div className="user-form-grid">
                <div className="user-form-group">
                  <label htmlFor="email" className="user-form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="user-form-input"
                    placeholder="user@example.com"
                    disabled={!!editingUser}
                    required
                  />
                  {editingUser && (
                    <p className="user-form-hint">Email cannot be changed after user creation</p>
                  )}
                </div>

                <div className="user-form-group">
                  <label htmlFor="firstName" className="user-form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="user-form-input"
                    placeholder="John"
                    required
                  />
                </div>

                <div className="user-form-group">
                  <label htmlFor="lastName" className="user-form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="user-form-input"
                    placeholder="Smith"
                    required
                  />
                </div>

                {/* Info about temporary password */}
                {!editingUser && (
                  <div className="user-info-box">
                    <p>A temporary password will be automatically generated and sent to the user's email. They will be required to change it on first login.</p>
                  </div>
                )}

                {/* Hotel Assignment */}
                <div className="user-form-group-full">
                  <label className="user-form-label">
                    Assigned Hotels *
                  </label>
                  
                  <div className="user-form-option">
                    <label className="user-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.assignedHotels === 'All'}
                        onChange={(e) => handleAllHotelsChange(e.target.checked)}
                        className="user-checkbox"
                      />
                      <span className="user-checkbox-text">All Hotels (Chain Admin)</span>
                    </label>
                  </div>

                  {formData.assignedHotels !== 'All' && (
                    <div className="user-hotels-list">
                      {hotels.length > 0 ? (
                        hotels.map((hotel) => {
                          const currentHotels = Array.isArray(formData.assignedHotels) ? formData.assignedHotels : [];
                          const hotelAssignment = currentHotels.find(h =>
                            typeof h === 'object' ? h.hotelid === hotel.hotelid : h === hotel.hotelid
                          );
                          const isChecked = !!hotelAssignment;
                          const currentPermission = typeof hotelAssignment === 'object' ? hotelAssignment.permission : 'User';

                          return (
                            <div key={hotel.hotelid} className="user-hotel-item">
                              <label className="user-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => handleHotelAssignmentChange(hotel.hotelid, e.target.checked)}
                                  className="user-checkbox"
                                />
                                <span className="user-checkbox-text">
                                  {hotel.hotelname} ({hotel.hotelcode})
                                </span>
                              </label>
                              {isChecked && (
                                <select
                                  value={currentPermission}
                                  onChange={(e) => handlePermissionChange(hotel.hotelid, e.target.value)}
                                  className="user-permission-select"
                                >
                                  <option value="User">User</option>
                                  <option value="Admin">Admin</option>
                                </select>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="user-no-hotels">
                          No hotels available. Please create hotels first.
                        </div>
                      )}
                    </div>
                  )}

                  {formData.assignedHotels === 'All' && (
                    <div className="user-info-box">
                      <p>This user will have access to all hotels in the chain.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="user-form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="btn-cancel-form"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-save-form"
                >
                  <Save className="btn-icon" />
                  {saving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSettings;
